import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import { MilestonePhaseItemDto } from 'src/api/FiretrolApiClient';

export const MILESTONE_ITEM_DEFAULT_SORTING: MRT_SortingState = [
  { id: 'phase.order', desc: false },
  { id: 'order', desc: false },
];

/** Items without a phase share one group per job or template. */
export const milestoneItemGroupKey = (item: MilestonePhaseItemDto) =>
  item.phase?.id ?? '';

/** MRT evaluates sorting against empty skeleton rows while loading, so fields can be missing at runtime. */
const compareText = (a: string | undefined, b: string | undefined) => {
  const left = (a ?? '').toUpperCase();
  const right = (b ?? '').toUpperCase();
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

/** Typed as Date, but the generated client has no JSON reviver, so it arrives as an ISO string. */
const timeOf = (value: Date | string | undefined) =>
  value ? new Date(value).getTime() : 0;

/**
 * Mirrors the server's in-group order. Orders can tie or skip numbers until
 * the group's first move renumbers it, so neighbours come from this sort,
 * never from `order ± 1`.
 */
export const compareMilestoneItems = (
  a: MilestonePhaseItemDto,
  b: MilestonePhaseItemDto,
) =>
  a.order - b.order ||
  compareText(a.title, b.title) ||
  timeOf(a.createdAt) - timeOf(b.createdAt) ||
  compareText(a.id, b.id);

export interface IMilestoneItemMoveBounds {
  isFirst: boolean;
  isLast: boolean;
}

export const getMilestoneItemMoveBounds = (
  items: MilestonePhaseItemDto[],
): Map<string, IMilestoneItemMoveBounds> => {
  const groups = new Map<string, MilestonePhaseItemDto[]>();
  for (const item of items) {
    const key = milestoneItemGroupKey(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  const bounds = new Map<string, IMilestoneItemMoveBounds>();
  for (const group of groups.values()) {
    const sorted = [...group].sort(compareMilestoneItems);
    sorted.forEach((item, index) =>
      bounds.set(item.id, {
        isFirst: index === 0,
        isLast: index === sorted.length - 1,
      }),
    );
  }
  return bounds;
};

export const isMilestoneItemDefaultSorting = (sorting: MRT_SortingState) =>
  sorting.length === MILESTONE_ITEM_DEFAULT_SORTING.length &&
  sorting.every(
    (sort, index) =>
      sort.id === MILESTONE_ITEM_DEFAULT_SORTING[index].id &&
      sort.desc === MILESTONE_ITEM_DEFAULT_SORTING[index].desc,
  );

const hasFilterValue = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some(hasFilterValue)
    : value !== undefined && value !== null && value !== '';

/** A filter can hide a row's in-phase neighbour, so a move would swap with a row the user cannot see. */
export const isMilestoneItemFilterActive = (
  columnFilters: MRT_ColumnFiltersState,
  globalFilter: string | undefined,
) => !!globalFilter || columnFilters.some((f) => hasFilterValue(f.value));

export interface IMilestoneItemRow {
  original: MilestonePhaseItemDto;
}

const phaseOrderOf = (row: IMilestoneItemRow) =>
  row.original.phase?.order ?? Number.POSITIVE_INFINITY;

/** Phases that share an order still stay contiguous, so a phase's rows are never interleaved with another's. */
export const milestonePhaseOrderSortingFn = (
  a: IMilestoneItemRow,
  b: IMilestoneItemRow,
) =>
  phaseOrderOf(a) - phaseOrderOf(b) ||
  compareText(
    milestoneItemGroupKey(a.original),
    milestoneItemGroupKey(b.original),
  );

/** Sort-only column; tables must keep it hidden through `columnVisibility`. */
export const milestoneItemOrderColumn: MRT_ColumnDef<MilestonePhaseItemDto> = {
  id: 'order',
  header: 'Order',
  accessorFn: (row) => row.order,
  sortingFn: (a, b) => compareMilestoneItems(a.original, b.original),
  enableHiding: false,
  visibleInShowHideMenu: false,
  enableColumnFilter: false,
  enableGlobalFilter: false,
};
